import React, { useState } from "react";
import { useQuery, useMutation } from "convex/react";
import { api } from "../../convex/_generated/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { FileText, Trash2, Calendar, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import type { Id } from "../../convex/_generated/dataModel";

interface DraftsProps {
  onResumeDraft?: (
    draftId: Id<"property_drafts">,
    draftData: {
      base: any;
      types: {
        selected: string[];
        data: Record<string, any>;
      };
      facilities: any;
      contact: any;
      step: number;
    }
  ) => void;
}

export function Drafts({ onResumeDraft }: DraftsProps) {
  // @ts-expect-error - TypeScript may need Convex API types to be regenerated after adding drafts module
  const drafts = useQuery(api.drafts.getMyDrafts, {});
  // @ts-expect-error - TypeScript may need Convex API types to be regenerated after adding drafts module
  const deleteDraft = useMutation(api.drafts.deleteDraft);
  const [deletingId, setDeletingId] = useState<Id<"property_drafts"> | null>(
    null
  );
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const handleDelete = async (id: Id<"property_drafts">) => {
    setDeletingId(id);
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (!deletingId) return;

    try {
      await deleteDraft({ id: deletingId });
      toast.success("Draft deleted successfully");
      setDeletingId(null);
      setShowDeleteDialog(false);
    } catch (error) {
      console.error("Failed to delete draft:", error);
      toast.error("Failed to delete draft. Please try again.");
    }
  };

  const formatDate = (timestamp: number) => {
    const date = new Date(timestamp);
    return date.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStepName = (step: number) => {
    const stepNames: Record<number, string> = {
      1: "Basic Information",
      2: "Property Type",
      3: "Type Details",
      4: "Facilities",
      5: "Images",
      6: "Review",
      7: "Contact",
    };
    return stepNames[step] || `Step ${step}`;
  };

  if (!drafts) {
    return (
      <div className="w-full max-w-none space-y-6 overflow-y-auto max-h-[calc(100vh-200px)] p-6 min-h-screen">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  if (drafts.length === 0) {
    return (
      <div className="w-full max-w-none space-y-6 overflow-y-auto max-h-[calc(100vh-200px)] p-6 min-h-screen">
        <div>
          <h3 className="text-2xl font-bold tracking-tight">Property Drafts</h3>
          <p className="text-muted-foreground">
            Manage your saved property drafts
          </p>
        </div>
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <FileText className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium text-muted-foreground mb-2">
              No drafts found
            </h3>
            <p className="text-sm text-muted-foreground text-center max-w-md">
              Start creating a property and your progress will be automatically
              saved as a draft. You can also manually save drafts using the
              "Save Draft" button.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="w-full max-w-none space-y-6 overflow-y-auto max-h-[calc(100vh-200px)] p-6 min-h-screen">
      <div>
        <h3 className="text-2xl font-bold tracking-tight">Property Drafts</h3>
        <p className="text-muted-foreground">
          Manage your saved property drafts. Click resume to continue editing.
        </p>
      </div>

      <div className="grid gap-4">
        {drafts.map((draft) => {
          let draftData: {
            base?: { property_name?: string };
            step?: number;
          } = {};
          try {
            draftData = JSON.parse(draft.data);
          } catch {
            // ignore parse errors
          }

          return (
            <Card key={draft._id} className="hover:shadow-md transition-shadow">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="text-lg mb-1">
                      {draft.property_name || "Untitled Property"}
                    </CardTitle>
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      <div className="flex items-center gap-1.5">
                        <Calendar className="h-4 w-4" />
                        {formatDate(draft.updated_at)}
                      </div>
                      <div className="flex items-center gap-1.5">
                        <ArrowRight className="h-4 w-4" />
                        {getStepName(draft.step)}
                      </div>
                    </div>
                    {draft.source_notes !== undefined && (
                      <p className="mt-2 whitespace-pre-wrap text-sm text-muted-foreground">
                        Notes from the CRM, for staff only: {draft.source_notes}
                      </p>
                    )}
                    {draft.source_photo_urls !== undefined &&
                      draft.source_photo_urls.length > 0 && (
                        <div className="flex flex-wrap items-center gap-3 mt-2 text-sm text-muted-foreground">
                          <span>Photos from the CRM:</span>
                          {draft.source_photo_urls.map((url, index) => (
                            <a
                              key={url}
                              href={url}
                              target="_blank"
                              rel="noreferrer"
                              className="underline hover:text-foreground"
                            >
                              Photo {index + 1}
                            </a>
                          ))}
                        </div>
                      )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        let parsedData: {
                          base?: any;
                          types?: {
                            selected?: string[];
                            data?: Record<string, any>;
                          };
                          facilities?: any;
                          contact?: any;
                          step?: number;
                        } = {};
                        try {
                          parsedData = JSON.parse(draft.data);
                        } catch (error) {
                          toast.error("Failed to load draft data");
                          console.error("Failed to parse draft data:", error);
                          return;
                        }

                        if (
                          onResumeDraft &&
                          parsedData.base &&
                          parsedData.types
                        ) {
                          onResumeDraft(draft._id, {
                            base: parsedData.base,
                            types: {
                              selected: parsedData.types.selected || [],
                              data: parsedData.types.data || {},
                            },
                            facilities: parsedData.facilities || {},
                            contact: parsedData.contact || {},
                            step: parsedData.step || 1,
                          });
                        } else {
                          toast.error("Invalid draft data format");
                        }
                      }}
                      className="active:scale-95 transition-all duration-150 ease-out cursor-pointer"
                    >
                      Resume
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDelete(draft._id)}
                      className="text-destructive hover:text-destructive hover:bg-destructive/10 active:scale-95 transition-all duration-150 ease-out cursor-pointer"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
            </Card>
          );
        })}
      </div>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Draft</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this draft? This action cannot be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => {
                setDeletingId(null);
                setShowDeleteDialog(false);
              }}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
